/**
 * Number formatting for the shell screens. Deterministic (no Intl): ICU data
 * differs between browsers, and CLDR's en-ZA uses a decimal comma, while the
 * design wants English with a decimal point and Afrikaans with a decimal comma
 * (docs/GAME_DESIGN.md § 12). Both languages group thousands with a
 * non-breaking space, as South African style guides do ("12 345").
 */
import type { Locale } from '@dropstack/core';

/** Non-breaking space: a score never wraps between its digit groups. */
const GROUP_SEPARATOR = ' ';
/** Typographic minus, matching core formatRational(). */
const MINUS = '−';

const decimalMark = (locale: Locale): string => (locale === 'af' ? ',' : '.');

function groupDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
}

/** A whole number with grouped thousands: 12345 → "12 345", -7 → "−7". Non-finite → "–". */
export function formatInteger(n: number): string {
  if (!Number.isFinite(n)) return '–';
  const rounded = Math.round(n);
  const sign = rounded < 0 ? MINUS : '';
  return sign + groupDigits(String(Math.abs(rounded)));
}

/** A decimal with `digits` fraction digits in the locale's style: 4.25 → "4.3" (en) / "4,3" (af). */
export function formatDecimal(n: number, locale: Locale, digits = 1): string {
  if (!Number.isFinite(n)) return '–';
  const fixed = Math.abs(n).toFixed(digits);
  const [whole = '0', fraction] = fixed.split('.');
  const sign = n < 0 && Number(fixed) !== 0 ? MINUS : '';
  return sign + groupDigits(whole) + (fraction ? decimalMark(locale) + fraction : '');
}

/** A 0–1 fraction as a whole percentage: 0.816 → "82%". Clamped to 0–100%. */
export function formatPercent(fraction: number): string {
  if (!Number.isFinite(fraction)) return '–';
  return `${Math.round(Math.min(1, Math.max(0, fraction)) * 100)}%`;
}

/**
 * A response time: one decimal under 10 s ("4.2 s" / "4,2 s"), whole seconds
 * up to a minute ("37 s"), then minutes and seconds ("1 min 05 s").
 */
export function formatSeconds(ms: number, locale: Locale): string {
  if (!Number.isFinite(ms) || ms < 0) return '–';
  const seconds = ms / 1000;
  if (seconds < 9.95) return `${formatDecimal(seconds, locale, 1)} s`;
  const whole = Math.round(seconds);
  if (whole < 60) return `${whole} s`;
  const minutes = Math.floor(whole / 60);
  return `${minutes} min ${String(whole % 60).padStart(2, '0')} s`;
}

/** Run length as a clock: 205 000 ms → "3:25", 3 725 000 ms → "1:02:05". */
export function formatClock(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '–';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}
